import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { hasDashboardFullAccess } from '../../utils/workspaces';
import Swal from 'sweetalert2';
import Select from 'react-select';
import DashboardLayout from '../../components/DashboardLayout';
import axiosInstance from '../../utils/axiosInstance';
import AuthService from '../../services/authService';

const STATUS_META = {
  'pending to solve': { label: 'Pending to solve', badge: 'bg-gray-100 text-gray-700', border: 'border-l-gray-400' },
  'in progress': { label: 'In progress', badge: 'bg-blue-100 text-blue-700', border: 'border-l-blue-400' },
  solved: { label: 'Solved', badge: 'bg-green-100 text-green-700', border: 'border-l-green-500' },
};
const STATUS_OPTIONS = ['pending to solve', 'in progress', 'solved'];

const SELECT_STYLES = {
  control: (base, state) => ({
    ...base,
    borderRadius: '0.5rem',
    borderColor: state.isFocused ? '#818cf8' : '#d1d5db',
    boxShadow: state.isFocused ? '0 0 0 2px #a5b4fc' : 'none',
    fontSize: '0.875rem',
    minHeight: '38px',
    '&:hover': { borderColor: '#818cf8' },
  }),
  option: (base, state) => ({
    ...base,
    fontSize: '0.875rem',
    backgroundColor: state.isSelected ? '#4f46e5' : state.isFocused ? '#eef2ff' : 'white',
    color: state.isSelected ? 'white' : '#111827',
  }),
  menu: (base) => ({ ...base, borderRadius: '0.5rem', zIndex: 50 }),
};

const EMPTY_FORM = { title: '', description: '', status: 'pending to solve', assignedTo: null };

function fmtDateTime(value) {
  if (!value) return '—';
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true,
  }).format(new Date(value));
}

function TicketCard({ ticket, isSuperAdmin, currentStaffId, staffOptions, onUpdate, onDelete, onAddComment, onDeleteComment }) {
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [newComment, setNewComment] = useState('');
  const [busy, setBusy] = useState(false);

  const meta = STATUS_META[ticket.status] || STATUS_META['pending to solve'];
  const isCreator = ticket.createdBy === currentStaffId;
  const isAssignee = ticket.assignedToId === currentStaffId;
  // Once solved, the ticket is locked for everyone except a super admin --
  // the creator (and assignee) permanently lose edit/delete/status rights.
  const isLocked = ticket.status === 'solved' && !isSuperAdmin;
  const canEdit = !isLocked && (isSuperAdmin || isCreator);
  const canChangeStatus = !isLocked && (isSuperAdmin || isCreator || isAssignee);
  const canComment = !isLocked && (isSuperAdmin || isAssignee);

  const startEdit = () => {
    setEditForm({
      title: ticket.title,
      description: ticket.description,
      status: ticket.status,
      assignedTo: ticket.assignedToId,
    });
    setEditing(true);
  };

  const saveEdit = async () => {
    setBusy(true);
    try {
      await onUpdate(ticket.ticketId, editForm);
      setEditing(false);
    } finally {
      setBusy(false);
    }
  };

  const changeStatus = async (status) => {
    if (status === 'solved') {
      const result = await Swal.fire({
        icon: 'warning',
        title: 'Mark this issue as solved?',
        text: 'Once solved, this issue is locked -- you will no longer be able to edit, delete, or comment on it. Only a super admin can change it after this.',
        showCancelButton: true,
        confirmButtonText: 'Yes, mark solved',
        confirmButtonColor: '#16a34a',
        cancelButtonText: 'Cancel',
      });
      if (!result.isConfirmed) return;
    }
    setBusy(true);
    try {
      await onUpdate(ticket.ticketId, { statusOnly: status });
    } finally {
      setBusy(false);
    }
  };

  const submitComment = async () => {
    if (!newComment.trim()) return;
    setBusy(true);
    try {
      await onAddComment(ticket.ticketId, newComment.trim());
      setNewComment('');
    } finally {
      setBusy(false);
    }
  };

  const assigneeOption = staffOptions.find((o) => o.value === editForm?.assignedTo) || null;

  return (
    <div className={`rounded-xl border border-gray-200 border-l-4 ${meta.border} bg-white p-4 shadow-sm`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <button type="button" onClick={() => setExpanded((v) => !v)} className="min-w-0 flex-1 text-left">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-semibold text-gray-900">{ticket.title}</h3>
            <span className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${meta.badge}`}>{meta.label}</span>
            {isLocked && (
              <span className="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-bold text-gray-500">Locked</span>
            )}
          </div>
          <p className="mt-1 text-xs text-gray-500">
            Created by {ticket.creator?.name || '—'} · Assigned to {ticket.assignee?.name || '—'} · {fmtDateTime(ticket.createdAt)}
          </p>
        </button>
        <div className="flex shrink-0 items-center gap-1.5">
          {canChangeStatus && !editing && (
            <select
              value={ticket.status}
              onChange={(e) => changeStatus(e.target.value)}
              disabled={busy}
              className="rounded-lg border border-gray-300 px-2 py-1.5 text-xs"
            >
              {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{STATUS_META[s].label}</option>)}
            </select>
          )}
          {canEdit && !editing && (
            <button type="button" onClick={startEdit} className="rounded-lg border border-gray-300 px-2.5 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50">
              Edit
            </button>
          )}
          {canEdit && (
            <button type="button" onClick={() => onDelete(ticket.ticketId)} className="rounded-lg border border-red-200 bg-red-50 px-2.5 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-100">
              Delete
            </button>
          )}
        </div>
      </div>

      {editing ? (
        <div className="mt-4 space-y-3 border-t border-gray-100 pt-4">
          <div>
            <label className="mb-1 block text-xs font-semibold text-gray-700">Title</label>
            <input
              type="text"
              value={editForm.title}
              onChange={(e) => setEditForm((f) => ({ ...f, title: e.target.value }))}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold text-gray-700">Description</label>
            <textarea
              value={editForm.description}
              onChange={(e) => setEditForm((f) => ({ ...f, description: e.target.value }))}
              rows={3}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            />
          </div>
          {isSuperAdmin && (
            <div>
              <label className="mb-1 block text-xs font-semibold text-gray-700">Assigned to</label>
              <Select
                options={staffOptions}
                value={assigneeOption}
                onChange={(opt) => setEditForm((f) => ({ ...f, assignedTo: opt ? opt.value : null }))}
                classNamePrefix="rs"
                styles={SELECT_STYLES}
              />
            </div>
          )}
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setEditing(false)} className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50">
              Cancel
            </button>
            <button type="button" onClick={saveEdit} disabled={busy} className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50">
              {busy ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      ) : expanded ? (
        <div className="mt-4 border-t border-gray-100 pt-4">
          <p className="whitespace-pre-wrap text-sm text-gray-700">{ticket.description}</p>

          {ticket.statusLog?.length > 0 && (
            <div className="mt-4">
              <p className="mb-2 text-xs font-semibold text-gray-500">History</p>
              <ol className="space-y-1.5 border-l-2 border-gray-200 pl-3">
                {ticket.statusLog.map((log) => (
                  <li key={log.logId} className="text-xs text-gray-600">
                    <span className={`mr-1.5 rounded-full px-2 py-0.5 text-[10px] font-bold ${(STATUS_META[log.status] || STATUS_META['pending to solve']).badge}`}>
                      {(STATUS_META[log.status] || STATUS_META['pending to solve']).label}
                    </span>
                    by {log.changer?.name || 'Unknown'} · {fmtDateTime(log.changedAt)}
                  </li>
                ))}
              </ol>
            </div>
          )}

          <div className="mt-4">
            <p className="mb-2 text-xs font-semibold text-gray-500">Comments ({ticket.comments.length})</p>
            <div className="space-y-2">
              {ticket.comments.length === 0 && <p className="text-xs text-gray-400">No comments yet.</p>}
              {ticket.comments.map((c) => (
                <div key={c.commentId} className="rounded-lg bg-gray-50 p-2.5 text-xs">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-semibold text-gray-700">{c.creator?.name || 'Unknown'}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-gray-400">{fmtDateTime(c.createdAt)}</span>
                      {!isLocked && (isSuperAdmin || c.createdBy === currentStaffId) && (
                        <button type="button" onClick={() => onDeleteComment(ticket.ticketId, c.commentId)} className="text-red-500 hover:text-red-700">
                          Delete
                        </button>
                      )}
                    </div>
                  </div>
                  <p className="mt-1 whitespace-pre-wrap text-gray-700">{c.comment}</p>
                </div>
              ))}
            </div>

            {canComment ? (
              <div className="mt-3 flex gap-2">
                <input
                  type="text"
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Add a comment…"
                  className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  onKeyDown={(e) => { if (e.key === 'Enter') submitComment(); }}
                />
                <button type="button" onClick={submitComment} disabled={busy} className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50">
                  Post
                </button>
              </div>
            ) : (
              <p className="mt-3 text-xs text-gray-400">
                {isLocked
                  ? 'This issue is solved and locked -- only a super admin can make further changes.'
                  : "Only the assigned person or a super admin can comment on this ticket."}
              </p>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default function IssueTracker() {
  const isSuperAdmin = hasDashboardFullAccess(AuthService.getUser());

  const [tickets, setTickets] = useState([]);
  const [currentStaffId, setCurrentStaffId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [staffOptions, setStaffOptions] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [statusFilter, setStatusFilter] = useState('pending to solve');

  const loadTickets = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get('/admin/issue-tracker');
      setTickets(res.data?.tickets || []);
      setCurrentStaffId(res.data?.currentStaffId || null);
    } catch (err) {
      setTickets([]);
      Swal.fire('Error', err?.response?.data?.message || 'Failed to load tickets.', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadStaffOptions = useCallback(async () => {
    try {
      const res = await axiosInstance.get('/admin/issue-tracker/staff-options');
      setStaffOptions((res.data?.staff || []).map((s) => ({ value: s.staffId, label: s.name })));
    } catch {
      setStaffOptions([]);
    }
  }, []);

  useEffect(() => { loadTickets(); }, [loadTickets]);
  useEffect(() => { if (isSuperAdmin) loadStaffOptions(); }, [isSuperAdmin, loadStaffOptions]);

  const openForm = () => {
    setFormData(EMPTY_FORM);
    setShowForm(true);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!formData.title.trim() || !formData.description.trim()) return;
    setSubmitting(true);
    try {
      await axiosInstance.post('/admin/issue-tracker', formData);
      setShowForm(false);
      await loadTickets();
    } catch (err) {
      Swal.fire('Error', err?.response?.data?.message || 'Failed to create ticket.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdate = async (ticketId, payload) => {
    try {
      if (payload.statusOnly) {
        await axiosInstance.put(`/admin/issue-tracker/${ticketId}/status`, { status: payload.statusOnly });
      } else {
        await axiosInstance.put(`/admin/issue-tracker/${ticketId}`, payload);
      }
      await loadTickets();
    } catch (err) {
      Swal.fire('Error', err?.response?.data?.message || 'Failed to update ticket.', 'error');
    }
  };

  const handleDelete = async (ticketId) => {
    const result = await Swal.fire({
      icon: 'warning',
      title: 'Delete this ticket?',
      showCancelButton: true,
      confirmButtonText: 'Delete',
      confirmButtonColor: '#dc2626',
      cancelButtonText: 'Cancel',
    });
    if (!result.isConfirmed) return;
    try {
      await axiosInstance.delete(`/admin/issue-tracker/${ticketId}`);
      await loadTickets();
    } catch (err) {
      Swal.fire('Error', err?.response?.data?.message || 'Failed to delete ticket.', 'error');
    }
  };

  const handleAddComment = async (ticketId, comment) => {
    try {
      await axiosInstance.post(`/admin/issue-tracker/${ticketId}/comments`, { comment });
      await loadTickets();
    } catch (err) {
      Swal.fire('Error', err?.response?.data?.message || 'Failed to add comment.', 'error');
    }
  };

  const handleDeleteComment = async (ticketId, commentId) => {
    try {
      await axiosInstance.delete(`/admin/issue-tracker/${ticketId}/comments/${commentId}`);
      await loadTickets();
    } catch (err) {
      Swal.fire('Error', err?.response?.data?.message || 'Failed to delete comment.', 'error');
    }
  };

  const assigneeOption = useMemo(
    () => staffOptions.find((o) => o.value === formData.assignedTo) || null,
    [staffOptions, formData.assignedTo]
  );

  const statusCounts = useMemo(() => {
    const counts = { all: tickets.length, 'pending to solve': 0, 'in progress': 0, solved: 0 };
    tickets.forEach((t) => { counts[t.status] = (counts[t.status] || 0) + 1; });
    return counts;
  }, [tickets]);

  const filteredTickets = useMemo(() => {
    if (statusFilter === 'all') return tickets;
    return tickets.filter((t) => t.status === statusFilter);
  }, [tickets, statusFilter]);

  return (
    <DashboardLayout title="Issue Tracker">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Issue Tracker</h1>
          <p className="mt-1 text-sm text-gray-600">
            Log an issue or problem. Only you, whoever it's assigned to, and super admins can see it.
          </p>
        </div>
        <button type="button" onClick={openForm} className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700">
          + New Issue
        </button>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-1 rounded-lg bg-gray-100 p-1 text-sm font-semibold">
        {['all', ...STATUS_OPTIONS].map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setStatusFilter(s)}
            className={`rounded-md px-3 py-1.5 ${statusFilter === s ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'}`}
          >
            {s === 'all' ? 'All' : STATUS_META[s].label} ({statusCounts[s] || 0})
          </button>
        ))}
      </div>

      {loading ? (
        <p className="text-sm text-gray-400">Loading…</p>
      ) : filteredTickets.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-8 text-center text-sm text-gray-500">
          {tickets.length === 0 ? 'No issues yet.' : 'No issues match this status.'}
        </div>
      ) : (
        <div className="space-y-3">
          {filteredTickets.map((ticket) => (
            <TicketCard
              key={ticket.ticketId}
              ticket={ticket}
              isSuperAdmin={isSuperAdmin}
              currentStaffId={currentStaffId}
              staffOptions={staffOptions}
              onUpdate={handleUpdate}
              onDelete={handleDelete}
              onAddComment={handleAddComment}
              onDeleteComment={handleDeleteComment}
            />
          ))}
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-lg rounded-xl bg-white p-6 shadow-xl">
            <h2 className="text-lg font-bold text-gray-900">New Issue</h2>
            <form onSubmit={handleCreate} className="mt-4 space-y-4">
              <div>
                <label className="mb-1 block text-sm font-semibold text-gray-700">Title</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData((f) => ({ ...f, title: e.target.value }))}
                  required
                  maxLength={255}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-semibold text-gray-700">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData((f) => ({ ...f, description: e.target.value }))}
                  required
                  rows={4}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-semibold text-gray-700">Assigned to</label>
                {isSuperAdmin ? (
                  <Select
                    options={staffOptions}
                    value={assigneeOption}
                    onChange={(opt) => setFormData((f) => ({ ...f, assignedTo: opt ? opt.value : null }))}
                    placeholder="Assign to a staff member…"
                    classNamePrefix="rs"
                    styles={SELECT_STYLES}
                  />
                ) : (
                  <p className="rounded-lg bg-gray-50 px-3 py-2 text-sm text-gray-500">
                    Assigned to you — only a super admin can hand an issue to someone else.
                  </p>
                )}
              </div>
              <div>
                <label className="mb-1 block text-sm font-semibold text-gray-700">Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData((f) => ({ ...f, status: e.target.value }))}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                >
                  {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{STATUS_META[s].label}</option>)}
                </select>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setShowForm(false)} className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50">
                  Cancel
                </button>
                <button type="submit" disabled={submitting} className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50">
                  {submitting ? 'Creating…' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
